import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import {
    PoisonRequeueResult,
    requeuePoisonQueue,
} from '../../api/poisonRequeue';
import { describeError } from '../../api/requeue';
import { formatCount } from '../../api/queueMonitor';

export interface IPoisonRequeuePanelProps {
    customer?: string;
    environment?: string;
    /** The poison queue that is open. */
    poisonQueue: string;
    /** The queue these messages broke on, and therefore go back to. */
    targetQueue: string;
    /** How many are in it right now. */
    depth: number;
    /** Call as soon as something moved, so the queue state is measured again. */
    onMoved: () => void;
}

/** The result in plain language. The API summary is meant for log lines, not for users. */
const describeResult = (result: PoisonRequeueResult): string => {
    if (result.read === 0) {
        return result.error ?? 'Deze queue was al leeg.';
    }

    let text = `${formatCount(result.moved)} van ${formatCount(
        result.read
    )} bericht(en) teruggezet in ${result.targetQueue}.`;

    if (result.failed > 0) {
        text += ` ${formatCount(result.failed)} kon niet en staat nog in ${
            result.poisonQueue
        }.`;
    }

    if ((result.blocked ?? 0) > 0) {
        text += ` ${formatCount(
            result.blocked ?? 0
        )} bewust laten staan: opnieuw aanbieden kan daar niets oplossen, zou iets dubbel boeken of zet het dubbel op de queue. Waarom staat hieronder.`;
    }

    if (result.duplicated > 0) {
        text += ` ${formatCount(
            result.duplicated
        )} wordt twee keer aangeboden; dat is onschuldig, want een object dat al in Exact staat wordt overgeslagen.`;
    }

    // Only offer to continue when something actually succeeded. Whatever could not be
    // moved is invisible for a few minutes, so a second click would achieve nothing.
    if (result.moreLeft && result.moved > 0) {
        text += ` Er staan nog ${formatCount(
            result.poisonDepthAfter
        )} bericht(en); klik nog een keer voor de volgende ronde.`;
    }

    return text;
};

/**
 * Why messages stayed behind, once per reason: the API says it per message, and a count
 * alone does not tell whether to wait, check Exact or look elsewhere.
 */
export const reasonsLeftBehind = (result: PoisonRequeueResult): string[] =>
    Array.from(
        new Set(
            (result.sample ?? [])
                .filter((item) => !item.moved && !!item.error)
                .map((item) => item.error as string)
        )
    );

/**
 * Putting failed messages back on the queue they broke on.
 *
 * Deliberately two clicks: this is the only button on this screen that sets thousands of
 * messages in motion, and the first click explains what is about to happen.
 *
 * The warning above it is not a formality. Putting messages back repairs nothing - if the
 * cause is still there, the same messages break again and are back here a few minutes
 * later. Hence the reference to the Exact token: that is the usual reason a whole day of
 * invoices ends up in the poison queue.
 */
export const PoisonRequeuePanel = (props: IPoisonRequeuePanelProps) => {
    const axios = useAxiosSwagger();

    const [armed, setArmed] = useState<boolean>(false);
    const [busy, setBusy] = useState<boolean>(false);
    const [result, setResult] = useState<PoisonRequeueResult | null>(null);
    const [error, setError] = useState<string | null>(null);

    const disabled =
        !axios || !props.customer || !props.environment || props.depth <= 0;

    const run = async () => {
        if (disabled) {
            return;
        }

        setArmed(false);
        setBusy(true);
        setError(null);
        setResult(null);

        try {
            const outcome = await requeuePoisonQueue(
                axios!,
                props.customer!,
                props.environment!,
                props.poisonQueue
            );

            setResult(outcome);

            if (outcome.moved > 0) {
                props.onMoved();
            }
        } catch (e) {
            setError(describeError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="poison-requeue">
            <p className="poison-requeue__intro">
                <FontAwesomeIcon icon="triangle-exclamation" /> Terugzetten lost
                de oorzaak niet op. Staat die er nog, dan lopen deze berichten
                opnieuw stuk en staan ze een paar minuten later weer hier. Een
                verlopen Exact-token is de gebruikelijke reden; kijk daar dus
                eerst naar.
            </p>

            <div className="poison-requeue__actions">
                {!armed ? (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={disabled || busy}
                        onClick={() => setArmed(true)}
                        title={`Haalt de berichten uit ${props.poisonQueue} en zet ze terug op ${props.targetQueue}.`}
                    >
                        {busy ? (
                            <FontAwesomeIcon
                                icon="spinner"
                                spin
                                className="me-2"
                            />
                        ) : (
                            <FontAwesomeIcon
                                icon="rotate-left"
                                className="me-2"
                            />
                        )}
                        Terugzetten in {props.targetQueue}
                    </button>
                ) : (
                    <>
                        <span className="poison-requeue__confirm">
                            {formatCount(props.depth)} bericht(en) gaan van{' '}
                            {props.poisonQueue} naar {props.targetQueue}.
                        </span>

                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={run}
                        >
                            Ja, terugzetten
                        </button>

                        <button
                            type="button"
                            className="btn btn-link btn-sm"
                            onClick={() => setArmed(false)}
                        >
                            Annuleren
                        </button>
                    </>
                )}
            </div>

            {result && (
                <p
                    className={`poison-requeue__result${
                        result.moved === 0
                            ? ' poison-requeue__result--none'
                            : ''
                    }`}
                >
                    {describeResult(result)}
                </p>
            )}

            {result && reasonsLeftBehind(result).length > 0 && (
                <ul className="poison-requeue__reasons">
                    {reasonsLeftBehind(result).map((reason) => (
                        <li key={reason}>{reason}</li>
                    ))}
                </ul>
            )}

            {error && (
                <p className="poison-requeue__error" role="alert">
                    {error}
                </p>
            )}
        </div>
    );
};
